import { storeContainer } from 'src/models/Store/Store'
import { customerContainer } from 'src/models/Customer/Customer'
import { getUTMInfo, getImageFromProperty, getShoppingContext } from 'src/utils'
import {
  APPConfig,
  convertToBoolean,
  deleteFromLocalStorage,
  getLocalStorage,
  setLocalStorage,
} from 'config/appConfig'
import { getLocaleCodeFromUrl } from 'src/utils/localeUtils'
import { languageAbbreviation } from 'src/views/pages/LoginCallback/fixture'
import { isConsentAcceptedForTracking } from 'src/utils/consent'
import { BUNDLE, SKUKIT, FIXED_BUNDLE } from 'src/utils/constant'

/**
 * Tracks an event in the Insider analytics system
 *
 * @param {Object} options - The tracking options
 * @param {string} options.eventType - Type of event to track ('user', 'cart', 'product', etc.)
 * @param {Object} [options.eventData={}] - Data associated with the event
 * @returns {boolean} - Whether the tracking was successful
 */
function trackInsiderEvent(options) {
  const { eventType, eventData } = options || {}
  const insiderConfig = APPConfig?.getAppConfig()?.insider
  const enableDebugging = convertToBoolean(insiderConfig?.enableDebugging)
  const enableInsider = convertToBoolean(insiderConfig?.enableInsider)

  if (!isToTrackInsider(options)) {
    return false
  }

  if (enableDebugging) {
    console.log('Insider Event:', {
      type: eventType,
      ...eventData,
    })
  }

  const availableObjects = [
    'home',
    'category',
    'product',
    'listing',
    'user',
    'cart',
    'checkout',
    'product',
    'order',
    'page',
    'listing',
    'basket',
    'transaction',
    'purchase',
    'currency',
    'add_to_cart',
    'remove_from_cart',
    'sign_up_event',
    'custom_event',
  ]
  if (!availableObjects.includes(eventType)) {
    console.warn('Invalid object type:', eventType)
    return false
  }

  if (typeof window === 'undefined') {
    return false
  }

  createInsiderObject()

  const insiderObject = {
    type: eventType,
  }

  if (eventData) {
    insiderObject.value =
      typeof eventData === 'object' && !Array.isArray(eventData)
        ? { ...eventData }
        : eventData
  }

  window.InsiderQueue.push(insiderObject)

  return true
}

/**
 * Determines if Insider tracking should be enabled for the current user/session.
 *
 * Insider tracking is enabled if:
 * - The Insider feature is enabled in the app config.
 * - The current customer is a registered user.
 * - The user is either using the Stela app or has accepted consent for tracking.
 *
 * @param {Object} [options] - Optional parameters (currently unused).
 * @returns {boolean} True if Insider tracking should be performed, false otherwise.
 */
function isToTrackInsider(options) {
  const { eventType = '', enableTrackingWithoutConsent = false } = options || {}
  const insiderConfig = APPConfig?.getAppConfig()?.insider
  const enableInsider = convertToBoolean(insiderConfig?.enableInsider)
  const enableCookieConsentTracking = convertToBoolean(
    insiderConfig?.enableCookieConsentTracking
  )
  const locale = getLocaleCodeFromUrl()
  const country = locale ? locale.split('_')[1].toUpperCase() : ''
  const homeMarket =
    customerContainer?.getUserHomeCountry()?.toUpperCase() || ''
  const isHomeMarket = country == homeMarket

  if (enableTrackingWithoutConsent && enableInsider) {
    return true
  }

  if (
    enableInsider &&
    customerContainer?.isRegisterUser &&
    isHomeMarket &&
    (checkIsStelaOrVeraApp() ||
      isConsentAcceptedForTracking() ||
      enableCookieConsentTracking ||
      eventType == 'purchase' ||
      eventType == 'user')
  ) {
    return true
  }

  return false
}

/**
 * Checks if the current shopping context is a supported native app (Stela or Vera).
 *
 * Returns true if the shopping context indicates the user is in either the Stela mobile affiliate app
 * ('MOBILE_AFFILIATE') or the Vera mobile consumer app ('MOBILE_CONSUMER').
 *
 * @returns {boolean} True if the context is 'MOBILE_AFFILIATE' or 'MOBILE_CONSUMER' or 'skin_consult', false otherwise.
 */
function checkIsStelaOrVeraApp() {
  const shoppingContext = getShoppingContext()
  let contextText = shoppingContext?.context || ''
  let isNativeApp = false
  if (
    contextText === 'MOBILE_AFFILIATE' ||
    contextText === 'MOBILE_CONSUMER' ||
    contextText === 'skin_consult'
  ) {
    isNativeApp = true
  }
  return isNativeApp
}

/**
 * Creates the global insider_object if it doesn't exist
 */
function createInsiderObject() {
  window.InsiderQueue = window.InsiderQueue || []
}

// Set to true once trackInsiderUserObject fires (i.e. the profile API call has resolved).
let isProfileLoaded = false

// Buffers product event on direct PDP load until profile resolves; cleared on any non-PDP page nav.
let pendingProductEvent = null

/**
 * Initializes the Insider SDK
 *
 * @returns {boolean} - Whether initialization was successful
 */
function initInsider(options) {
  const insiderConfig = APPConfig?.getAppConfig()?.insider
  const enableDebugging = convertToBoolean(insiderConfig?.enableDebugging)
  const enableInsider = convertToBoolean(insiderConfig?.enableInsider)

  if (!isToTrackInsider(options)) {
    return false
  }

  if (enableDebugging) {
    console.log('Insider Event:', {
      type: 'init',
    })
  }
  window?.InsiderQueue?.push({
    type: 'init',
  })

  return true
}

/**
 * Tracks a currency change event in Insider
 *
 * @param {Object} options - Options for tracking currency change
 * @param {string} [options.currency=''] - The new currency value to track
 */
function trackCurrencyForInsider(options) {
  const { currency = '', enableTrackingWithoutConsent = false } = options || {}
  trackInsiderEvent({
    eventType: 'currency',
    eventData: currency,
    enableTrackingWithoutConsent,
  })
}

/**
 * Tracks a page view event in Insider
 *
 * @param {Object} options - The page view options
 * @param {string} options.pageType - Type of page being viewed
 */
function trackInsiderPageView(options) {
  pendingProductEvent = null
  const { pageType } = options || {}
  trackInsiderEvent({ eventType: pageType })
  initInsider()
}

/**
 * Tracks a catalog page visit in Insider
 *
 * @param {Object} options - The catalog tracking options
 * @param {Object} options.response - The catalog API response
 */
function trackInsiderCatalogPage(options) {
  pendingProductEvent = null
  const { response } = options || {}

  if (response?.product?.length > 0) {
    const categoryNames = getCategoryIds(response?.category)

    trackInsiderEvent({
      eventType: 'category',
      eventData: categoryNames,
    })

    trackCurrencyForInsider({ currency: response?.product?.[0]?.currency })
    initInsider()
  } else {
    return false
  }
}

/**
 * Extracts category names from a nested category structure
 *
 * This function recursively traverses the category hierarchy to collect all category names.
 * It processes the current category and all its parent categories, building a comprehensive
 * list of category names that represent the full category path.
 *
 * @param {Object} categories - The category object containing properties and parentCategories
 * @param {Object} categories.properties - Category properties including name
 * @param {string} categories.properties.name - The name of the category
 * @param {Array} categories.parentCategories - Array of parent category objects
 * @param {string} categories.identifier - Category identifier
 *
 * @returns {Array<string>} An array of category names from the current category and its parents
 *
 * @example
 * // Returns ["Tru Face", "Nu Skin", "Product Lines"]
 */
function getCategoryIds(categories) {
  if (!categories) return []

  const categoryNames = []

  if (categories.identifier && categories.identifier != 'top') {
    categoryNames.push(categories.identifier)
  }

  if (
    categories.parentCategories &&
    Array.isArray(categories.parentCategories)
  ) {
    for (const parentCategory of categories.parentCategories) {
      if (!parentCategory.identifier) {
        continue
      }

      const parentNames = getCategoryIds(parentCategory)
      categoryNames.push(...parentNames)
    }
  }

  return categoryNames
}

/**
 * Tracks a product page visit in Insider
 *
 * @param {Object} response - The product API response
 */
function trackInsiderProductPage({ response, suppressProductEvent = false }) {
  if (suppressProductEvent) {
    return
  }
  const product = response?.product?.[0]
  const item = getCatalogProducts({ product, category: response?.category })
  const currency = response?.product?.[0]?.currency

  // Buffer if profile API has not yet resolved; flushed in trackInsiderUserObject.
  // Last-wins: rapid PDP-to-PDP navigation replaces the buffer with the latest product.
  if (!isProfileLoaded) {
    pendingProductEvent = { item, currency }
    return
  }

  trackInsiderEvent({
    eventType: 'product',
    eventData: item,
  })
  trackCurrencyForInsider({ currency })
  initInsider()
}

/**
 * Transforms product data into the format required by Insider
 *
 * @param {Object} options - The product options
 * @param {Object} options.category - The product category
 * @param {Object} options.product - The product data
 * @returns {Object} Formatted product object for Insider
 */
function getCatalogProducts(options) {
  const { category, product } = options || {}
  let productDetails = product

  if (product?.sku?.length >= 1) {
    const defaultSkuId = productDetails?.properties?.defaultSkuId
    let selectedSku = product?.sku[0]
    if (defaultSkuId) {
      selectedSku = product?.sku?.find(sku => sku?.identifier === defaultSkuId)
    }
    productDetails = selectedSku
  }

  const regularPrice = productDetails?.totalValue?.originalPrice

  const priceAfterDiscount = productDetails?.totalValue?.priceAfterDiscount
  const categoryNames = getCategoryIds(category)

  return {
    id: productDetails?.identifier || '',
    name: productDetails?.properties?.name || '',
    taxonomy: categoryNames?.reverse() || [],
    currency: product?.currency || '',
    unit_price: regularPrice,
    unit_sale_price: priceAfterDiscount,
    url: getProductUrl({ item: product }),
    product_image_url: getImageFromProperty(productDetails?.properties),
  }
}

/**
 * Tracks cart events in Insider
 *
 * @param {Object} options - The cart tracking options
 * @param {Object} options.cartResponse - The cart API response
 */
function trackInsiderCartEvents(options) {
  const { eventType = 'cart', cartResponse } = options || {}
  const items = getCartProducts({ cartItems: cartResponse?.items, eventType })
  const totalValue = cartResponse?.value?.grandTotal
  const shippingCost = cartResponse?.value?.deliveryChargeIncludingTax
  const additionalPrices = {}

  if (totalValue) {
    additionalPrices.total = totalValue
  }

  if (shippingCost) {
    additionalPrices.shipping_cost = shippingCost
  }

  if (items?.length > 0) {
    trackInsiderEvent({
      eventType,
      eventData:
        eventType == 'cart'
          ? { items, ...additionalPrices }
          : { ...items?.[0], ...additionalPrices },
    })
    trackCurrencyForInsider({
      currency: storeContainer?.activeStoreResponse?.defaultCurrency,
    })
    initInsider()
  }
}

/**
 * Transforms cart items into the format required by Insider
 *
 * @param {Object} options - The cart product options
 * @param {Array} options.cartItems - The items in the cart
 * @param {boolean} [options.isFromOrderCreation=false] - Whether this is for order creation
 * @returns {Array} Formatted cart products for Insider
 */
function getCartProducts(options) {
  const {
    cartItems,
    isFromOrderCreation = false,
    orderId,
    source,
    category,
    channel,
  } = options || {}
  const cartProducts = cartItems?.map(item => {
    const sku =
      item?.skus?.find(s => {
        const type = s?.type?.toUpperCase() || ''
        return type !== BUNDLE && type !== SKUKIT && type !== FIXED_BUNDLE
      }) || item?.skus?.[0]

    const skuProperties = sku?.skuProperties || sku?.properties

    const transformedItem = {
      id: sku?.skuId,
      name: skuProperties?.name,
      taxonomy: [item?.properties?.categoryId] || [],
      unit_price: item?.unitValue?.originalPrice || sku?.price,
      unit_sale_price:
        item?.unitValue?.priceAfterDiscount || sku?.priceAfterMarkdown,
      url: getProductUrl({ item }),
      product_image_url: getImageFromProperty(skuProperties),
    }
    const utmInfo = getUTMInfo() || {}

    if (isFromOrderCreation && orderId) {
      transformedItem.custom = {
        source: source || 'Storefront',
        category: category || utmInfo?.utm_medium || 'Standard',
        channel: channel || 'Web',
        order_type: item?.isSubscription ? 'Subscription' : 'Standard',
      }
    }

    transformedItem.quantity = item?.quantity || 1
    return transformedItem
  })

  return cartProducts
}

/**
 * Generates a product URL for tracking purposes
 *
 * @param {Object} options - The URL options
 * @param {Object} options.item - The product item
 * @returns {string} The full product URL
 */
function getProductUrl(options) {
  const { item } = options || {}
  const localeCode = getLocaleCodeFromUrl()
  const localeCodePath = localeCode?.toLowerCase()?.split('_')
  const slugOrProductId = item?.properties?.slug || item?.id
  if (typeof window === 'undefined') {
    return ''
  }
  let url = `${window.location.origin}/${localeCodePath[1]}/${localeCodePath[0]}/product/${slugOrProductId}`
  const sku = item?.skus?.[0]

  if (sku?.skuProperties?.variantLabel) {
    const skuSlug = sku?.skuProperties?.slug || sku?.skuId || ''
    url = url + `?variant=${skuSlug}`
  }
  return url
}

/**
 * Tracks user authentication events in Insider.
 * Called unconditionally from App.js after getProfile() resolves for all user types
 * (registered and guest). Sets isProfileLoaded = true and flushes any product event
 * buffered during a direct PDP load.
 *
 * @param {Object} options - The user tracking options
 * @param {Object} options.userData - The user data
 * @param {string} options.eventType - Type of user event ('user' or 'signup')
 */
function trackInsiderUserObject(options) {
  isProfileLoaded = true
  const { userData, eventType } = options || {}
  const customerData = transformCustomerData(userData)
  const registeredEvents = {
    user: 'user',
    signup: 'custom_event',
  }
  const selectedEvent = registeredEvents[eventType]
  if (selectedEvent) {
    trackInsiderEvent({
      eventType: selectedEvent,
      eventData: customerData,
    })
  } else {
    console.warn('Invalid event type:', eventType)
  }

  // Flush product event buffered on direct PDP load before profile resolved.
  if (pendingProductEvent) {
    const { item, currency } = pendingProductEvent
    pendingProductEvent = null
    trackInsiderEvent({ eventType: 'product', eventData: item })
    trackCurrencyForInsider({ currency })
    initInsider()
  }
}

/**
 * Tracks a sign-up event in Insider
 *
 * This function sends a custom event to Insider with the event name 'sign_up_event'.
 * It is used to track user sign-up actions for analytics purposes.
 *
 * @param {Object} options - Optional parameters for tracking the sign-up event (currently unused)
 */
function trackInsiderSignupEvent(options) {
  // Added timeout to prevent Insider identifying it as page event instead of custom event
  setTimeout(() => {
    if (isToTrackInsider(options)) {
      window.InsiderQueue.push({
        type: 'custom_event',
        value: [
          {
            event_name: 'sign_up_event',
          },
        ],
      })
      initInsider()
    }
  }, 3000)
}

/**
 * Transforms customer data into the format required by Insider
 *
 * @param {Object} userData - The raw user data
 * @returns {Object} Formatted user object for Insider
 */
function transformCustomerData(userData) {
  const locale = getLocaleCodeFromUrl()
  const [language, country] = locale?.toLowerCase()?.split('_')

  const propertyValues = {
    userCountry: '',
    sponsorId: '',
    signupOrigin: '',
  }

  if (userData?.accounts?.properties) {
    for (const prop of userData.accounts.properties) {
      if (!prop) continue

      switch (prop.attributeId) {
        case 'countryCode':
          propertyValues.userCountry = prop.value
          break
        case 'sponsorId':
          propertyValues.sponsorId = prop.value
          break
        case 'signupOrigin':
          propertyValues.signupOrigin = prop.value
          break
        case 'signUpStatus':
          propertyValues.signUpStatus = prop.value
          break
      }
    }
  }

  let modifiedLocale = ''
  let modifiedLanguage = ''

  if (language == 'zh' || language == 'vi') {
    const defaultCountryForLanguage = {
      zh: 'CN',
      vi: 'VN',
    }
    modifiedLocale = `en_${country?.toUpperCase()}:${language}`
    modifiedLanguage = `${language}_${defaultCountryForLanguage[language]?.toUpperCase()}`
  } else {
    modifiedLocale = `${language}_${country?.toUpperCase()}`
    modifiedLanguage = `${language}_${country?.toUpperCase()}`
  }

  const transformedUser = {
    uuid: userData?.referenceAttribute || '',
    language: modifiedLanguage,
    locale: modifiedLocale,
  }

  if (!transformedUser?.uuid && userData?.referenceAttribute1) {
    transformedUser.custom = {
      primary_id: userData?.referenceAttribute1 || '',
    }
  }

  return transformedUser
}

/**
 * Sets Insider transaction data in local storage
 *
 * @param {Object} options - The transaction options
 * @param {string} options.orderId - The ID of the created order
 * @param {Object} options.cartResponse - The cart API response
 */
function setInsiderTransactionData(options) {
  const transactionData = getTransactionData(options)
  setLocalStorage('insiderTransactionData', transactionData)
}

/** * Retrieves Insider transaction data from local storage
 * @returns {Object} Insider transaction data
 */
function getInsiderTransactionData() {
  return getLocalStorage('insiderTransactionData')
}

/**
 * Tracks order creation events in Insider
 *
 */
function trackInsiderOrderCreation() {
  const transactionData = getInsiderTransactionData()
  if (transactionData) {
    const options = {
      eventType: 'purchase',
      eventData: transactionData,
      enableTrackingWithoutConsent: true,
    }
    trackInsiderEvent(options)
    trackCurrencyForInsider({
      currency: storeContainer?.activeStoreResponse?.defaultCurrency,
      enableTrackingWithoutConsent: true,
    })
    initInsider(options)
  }
  deleteFromLocalStorage('insiderTransactionData')
}

/**
 * Transforms order data into the format required by Insider
 *
 * @param {Object} options - The transaction options
 * @param {string} options.orderId - The ID of the created order
 * @param {Object} options.cartResponse - The cart API response
 * @returns {Object} Formatted transaction object for Insider
 */
function getTransactionData(options) {
  const { orderId, cartResponse, source, category, channel } = options || {}
  const items = getCartProducts({
    cartItems: cartResponse?.items,
    isFromOrderCreation: true,
    orderId,
    source,
    category,
    channel,
  })

  const paymentType = cartResponse?.payments?.[0]?.type || ''
  const orderCharges = cartResponse?.value
  const shippingCost = orderCharges?.totalDeliveryCharge || ''
  const grandTotal = orderCharges?.grandTotal || ''
  const currencyCode = cartResponse?.currencyCode || ''
  const deliveryDetails = cartResponse?.deliveryDetails?.address || {}

  return {
    order_id: orderId,
    currency: currencyCode,
    total: grandTotal,
    delivery: {
      country: deliveryDetails?.country,
      city: deliveryDetails?.city,
      district: deliveryDetails?.state,
    },
    payment_type: paymentType,
    items,
  }
}

/**
 * Resets the Insider session state. Call on logout or session invalidation
 * so a re-login on the same SPA page gets a fresh buffer and isProfileLoaded flag.
 */
function resetInsiderSession() {
  isProfileLoaded = false
  pendingProductEvent = null
}

export {
  trackInsiderEvent,
  trackInsiderPageView,
  trackInsiderCatalogPage,
  trackInsiderProductPage,
  trackInsiderCartEvents,
  trackInsiderUserObject,
  trackCurrencyForInsider,
  initInsider,
  createInsiderObject,
  trackInsiderOrderCreation,
  getCategoryIds,
  setInsiderTransactionData,
  getInsiderTransactionData,
  isToTrackInsider,
  checkIsStelaOrVeraApp,
  trackInsiderSignupEvent,
  resetInsiderSession,
}
